import { ChevronRight, Monitor, Moon, Search, Sun } from 'lucide-react';
import { Fragment } from 'react';
import { Link, useLocation } from 'react-router';

import { cn } from '@shared/ui/cn';
import { Kbd } from '@shared/ui/kbd';
import { type Theme } from '../providers/theme-context';
import { useTheme } from '../providers/use-theme';
import { useCrumbLabel } from '@shared/ui/crumb-context';
import { NAVIGATION } from './navigation';
import { UserMenu } from './user-menu';

export function Topbar({ onOpenCommands }: { onOpenCommands: () => void }) {
  return (
    // Translucida con desenfoque: al desplazar una tabla larga, el contenido
    // pasa por debajo en lugar de desaparecer bajo una barra opaca, lo que
    // mantiene la sensacion de un unico plano continuo.
    <header
      className={cn(
        'sticky top-0 z-10 flex h-(--spacing-header) shrink-0 items-center gap-4 px-5',
        'border-b border-line bg-panel/85 backdrop-blur-md',
        // Sombra tenue bajo la barra: al desplazar una tabla larga, separa lo
        // fijo de lo que se mueve por debajo. Sin ella los dos planos se
        // confunden en cuanto el contenido llega al borde.
        'shadow-[0_1px_3px_-1px_rgb(28_26_24/0.06)]',
      )}
    >
      {/*
        Tres zonas y no dos. Con la ruta a la izquierda y todo lo demas a la
        derecha quedaba medio ancho de pantalla vacio en medio, que es lo que
        hacia que la barra se viera sin terminar. El buscador ocupa el centro
        porque es lo que mas se usa de aqui: es la via mas rapida a cualquiera
        de las mil fichas del sistema.
      */}
      <div className="flex min-w-0 flex-1 items-center">
        <Breadcrumb />
      </div>

      <div className="hidden justify-center md:flex">
        <CommandTrigger onOpen={onOpenCommands} />
      </div>

      <div className="flex min-w-0 flex-1 items-center justify-end gap-3">
        <ThemeToggle />
        {/* Separador: sin el, el selector de tema y la cuenta se leen como un
            solo grupo de controles, y no lo son. */}
        <span className="h-6 w-px shrink-0 bg-line" aria-hidden="true" />
        <UserMenu />
      </div>
    </header>
  );
}

/**
 * Ruta de navegacion (spec 46).
 * Se deriva de la URL, no de un estado propio: un enlace compartido o un
 * refresco muestran siempre la misma ruta.
 */
function Breadcrumb() {
  const { pathname } = useLocation();
  // La ficha de un documento publica su numero: sin esto, el ultimo tramo de
  // /requerimientos/<uuid> seria el uuid, que no le dice nada a nadie.
  const publicada = useCrumbLabel();

  const segments = pathname.split('/').filter((segment) => segment.length > 0);

  const crumbs = segments.flatMap((segment, index) => {
    const to = `/${segments.slice(0, index + 1).join('/')}`;
    const ultimo = index === segments.length - 1;

    if (ultimo && publicada !== null) return [{ to, label: publicada }];

    const conocido = labelForPath(to);
    if (conocido !== undefined) return [{ to, label: conocido }];

    // Un identificador no es una miga: nadie reconoce un documento por su
    // uuid, y el segmento intermedio de `/proveedores/<uuid>/evaluaciones`
    // se mostraba crudo porque solo el ULTIMO admite etiqueta publicada. Se
    // omite: la ruta no se pierde, porque la miga siguiente la lleva entera.
    if (esIdentificador(segment)) return [];

    return [{ to, label: humanize(segment) }];
  });

  return (
    <nav aria-label="Ruta de navegacion" className="flex min-w-0 items-center gap-1.5 text-base">
      <Link
        to="/"
        className="shrink-0 text-fg-subtle transition-colors duration-(--t-tap) hover:text-fg"
      >
        Inicio
      </Link>

      {crumbs.map((crumb, index) => (
        <Fragment key={crumb.to}>
          <ChevronRight className="size-4 shrink-0 text-n300" aria-hidden="true" />
          {index === crumbs.length - 1 ? (
            <span aria-current="page" className="truncate font-semibold text-fg">
              {crumb.label}
            </span>
          ) : (
            <Link
              to={crumb.to}
              className="truncate text-fg-subtle transition-colors duration-(--t-tap) hover:text-fg"
            >
              {crumb.label}
            </Link>
          )}
        </Fragment>
      ))}
    </nav>
  );
}

/** Disparador de la paleta de comandos. Muestra su atajo siempre visible. */
function CommandTrigger({ onOpen }: { onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      // Deja de ser un boton discreto para parecerse a lo que es: la forma
      // mas rapida de llegar a cualquiera de las mil fichas del sistema. Con
      // ancho fijo, no encogido al texto, para que se lea como un campo.
      className={cn(
        'group flex h-9 w-80 items-center gap-2.5 rounded-md border border-line-strong lg:w-96',
        'bg-sunken pl-3.5 pr-1.5 text-sm text-fg-subtle',
        'transition-[border-color,background-color,color,box-shadow] duration-(--t-tap)',
        'hover:border-brand-line hover:bg-panel hover:text-fg-body',
        'hover:shadow-[0_1px_2px_0_rgb(28_26_24/0.06)]',
      )}
    >
      <Search className="size-4 shrink-0" aria-hidden="true" />
      <span className="flex-1 text-left">Buscar en el sistema</span>
      <Kbd className="group-hover:border-n300">Ctrl K</Kbd>
    </button>
  );
}

const THEME_OPTIONS: readonly { value: Theme; label: string; Icon: typeof Sun }[] = [
  { value: 'light', label: 'Tema claro', Icon: Sun },
  { value: 'system', label: 'Seguir al sistema', Icon: Monitor },
  { value: 'dark', label: 'Tema oscuro', Icon: Moon },
];

function ThemeToggle() {
  const { theme, setTheme } = useTheme();

  return (
    <div
      role="radiogroup"
      aria-label="Tema de la interfaz"
      className="flex items-center rounded-md border border-line-strong bg-sunken p-0.5"
    >
      {THEME_OPTIONS.map(({ value, label, Icon }) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={theme === value}
          aria-label={label}
          title={label}
          onClick={() => {
            setTheme(value);
          }}
          className={cn(
            'flex size-7 items-center justify-center rounded-sm transition-colors duration-(--t-tap)',
            theme === value
              ? 'bg-panel text-brand-ink shadow-[0_1px_2px_0_rgb(28_26_24/0.08)]'
              : 'text-fg-subtle hover:text-fg-body',
          )}
        >
          <Icon className="size-4" aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}

/** Un uuid o cualquier cosa que no se pueda leer en voz alta. */
function esIdentificador(segment: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu.test(segment);
}

function labelForPath(path: string): string | undefined {
  for (const group of NAVIGATION) {
    const match = group.items.find((item) => item.to === path);
    if (match !== undefined) return match.label;
  }
  return undefined;
}

function humanize(segment: string): string {
  const spaced = segment.replaceAll('-', ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
